import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/** M05 lookup dropdown row — the backend already applies sortOrder. */
interface PickerOption {
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly sortOrder: number;
}

export function ProfilePickerPage(): ReactNode {
  const { type } = useParams<{ type: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [opts, setOpts] = useState<PickerOption[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<unknown>(`/v1/master/lookup-categories/${encodeURIComponent(type ?? '')}/dropdown`, { token })
      .then((r) => { if (alive) setOpts(Array.isArray(r) ? (r as PickerOption[]) : []); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [type, token]);

  if (error) return <p role="alert">{error}</p>;
  if (!opts) return <p>{t('common.loading')}</p>;
  return (
    <main aria-label="profile-picker">
      <h1>{t('profile.picker.title', { type: type ?? '' })}</h1>
      {opts.length === 0 ? <p aria-label="picker-empty">{t('profile.picker.empty')}</p> : (
        <ul aria-label="picker-options">
          {opts.map((o) => (
            <li key={o.id}>
              <button type="button" onClick={() => setPicked(o.id)}>{o.description}</button>
              <span aria-label={`picker-code-${o.code}`}>{o.code}</span>
            </li>
          ))}
        </ul>
      )}
      {picked ? <p role="status">{t('profile.picker.selected', { id: picked })}</p> : null}
    </main>
  );
}
